import type { ReactElement } from 'react';
import type { SyncStatus } from '@lapo/core';
import { MaterialIcon } from './MaterialIcon.js';

/**
 * Headerless top icon toolbar (Logseq-style) — a top-left hamburger that toggles the left sidebar,
 * then right-justified quick actions (sync dot, search, settings) and, at the far right, a "panel"
 * icon that toggles the right-hand drawer. Icons are Google Material Symbols (referenced by name),
 * inheriting the theme color. Sync state shows as a small dot whose color is drawn from the theme
 * tokens so it reads in light and dark alike.
 */
const STATUS_COLOR: Record<SyncStatus, string> = {
  synced: '#2f9e57',
  syncing: 'var(--lapo-accent)',
  connecting: 'var(--lapo-accent)',
  offline: 'var(--lapo-faint)',
  error: '#d9534f',
};

function Icon({
  name,
  label,
  onClick,
  className,
  preserveFocus,
}: {
  name: string;
  label: string;
  onClick?: () => void;
  className?: string;
  /** Keep the editor focused/selected when pressed (for editing actions like indent). */
  preserveFocus?: boolean;
}): ReactElement {
  return (
    <button
      type="button"
      className={`lapo-icon-btn${className ? ` ${className}` : ''}`}
      title={label}
      aria-label={label}
      onClick={onClick}
      onMouseDown={preserveFocus ? (e) => e.preventDefault() : undefined}
    >
      <MaterialIcon name={name} size={20} />
    </button>
  );
}

/** SYSTEM-HEALTH — admin backend-health status layered onto the sync dot. Undefined for non-admins. */
export type HealthIndicator = 'ok' | 'degraded' | 'down' | 'unknown';

export interface IconBarProps {
  status: SyncStatus;
  /** SYSTEM-HEALTH (admin only). When set, the sync dot becomes a clickable health indicator that
   * pulses red on `down`, amber on `degraded`. Undefined (non-admins) → today's plain sync dot. */
  health?: HealthIndicator;
  /** Open the admin health detail panel. Only wired alongside {@link health}. */
  onHealthClick?: () => void;
  onSettings?: () => void;
  /** Open the global search palette. Rendered immediately to the right of the sync dot. */
  onSearch?: () => void;
  /** The top-left hamburger: collapse/expand the sidebar (desktop) or open/close the drawer (mobile). */
  onToggleSidebar?: () => void;
  /** The far-right "panel" icon: open/close the right-hand drawer (holds the Todoist Today view). */
  onToggleRight?: () => void;
  /** Whether the right drawer is open, so its toggle reads as active. */
  rightOpen?: boolean;
  /** Open/close the ✦ assistant popover (anchored to the header, not the drawer). */
  onAssistant?: () => void;
  /** Whether the assistant popover is open, so its header button reads as active. */
  assistantOpen?: boolean;
  /** Outdent/indent the focused bullet — rendered as a touch-only pair (no keyboard on mobile). */
  onOutdent?: () => void;
  onIndent?: () => void;
}

/** SYSTEM-HEALTH — the dot color/class when health is in play. `down` pulses red, `degraded` is amber;
 *  anything else falls back to the plain sync color so a healthy backend reads exactly as today. */
const HEALTH_CLASS: Record<HealthIndicator, string> = {
  down: ' lapo-health-critical',
  degraded: ' lapo-health-degraded',
  ok: '',
  unknown: '',
};

export function IconBar({
  status,
  health,
  onHealthClick,
  onSettings,
  onSearch,
  onToggleSidebar,
  onToggleRight,
  rightOpen,
  onAssistant,
  assistantOpen,
  onOutdent,
  onIndent,
}: IconBarProps): ReactElement {
  // Non-admins (health === undefined) get the exact original static dot. Admins get a clickable,
  // health-aware button that pulses on trouble.
  const healthDot =
    health === undefined ? (
      <span className="lapo-sync-dot" title={`Sync: ${status}`} style={{ background: STATUS_COLOR[status] }} />
    ) : (
      <button
        type="button"
        className={`lapo-sync-dot lapo-sync-dot-btn${HEALTH_CLASS[health]}`}
        title={`System health: ${health} · Sync: ${status}`}
        aria-label={`System health: ${health}. Open health details.`}
        onClick={onHealthClick}
        style={health === 'ok' || health === 'unknown' ? { background: STATUS_COLOR[status] } : undefined}
      />
    );
  return (
    <div className="lapo-iconbar" data-testid="iconbar" data-tauri-drag-region>
      <Icon label="Toggle sidebar" onClick={onToggleSidebar} name="menu" className="lapo-iconbar-sidebar" />
      {onOutdent ? <Icon label="Outdent" onClick={onOutdent} name="format_indent_decrease" className="lapo-iconbar-indent" preserveFocus /> : null}
      {onIndent ? <Icon label="Indent" onClick={onIndent} name="format_indent_increase" className="lapo-iconbar-indent" preserveFocus /> : null}
      {healthDot}
      {onAssistant ? (
        <Icon
          label="Assistant"
          onClick={onAssistant}
          name="auto_awesome"
          className={`lapo-iconbar-assistant${assistantOpen ? ' lapo-iconbar-active' : ''}`}
        />
      ) : null}
      {onSearch ? <Icon label="Search" onClick={onSearch} name="search" /> : null}
      <Icon label="Settings" onClick={onSettings} name="settings" />
      {onToggleRight ? (
        <Icon
          label="Toggle panel"
          onClick={onToggleRight}
          name="dock_to_left"
          className={`lapo-iconbar-right${rightOpen ? ' lapo-iconbar-active' : ''}`}
        />
      ) : null}
    </div>
  );
}
